import type { IWidget } from '@/interfaces/components/dashboard/widget'
import { useCompanyDashboardStore } from '@/store/companyDashboard'
import { useI18n } from '@/composable/useI18n'
import { DOOR_OPEN_STATE_COLORS, DOOR_OPEN_STATE_CLOSED, DOOR_OPEN_STATE_OPEN } from '@/utils/components/door-open-chart'
import { storeToRefs } from 'pinia'

export const STATUS_PANEL_ACCENT_COLOR = DOOR_OPEN_STATE_COLORS[DOOR_OPEN_STATE_OPEN]

export type StatusPanelState = 'open' | 'closed' | 'loading' | 'error' | 'unknown'

export interface IStatusPanelDisplay {
  state: StatusPanelState
  label: string
  accentColor: string
  bgClass: string
}

export function useStatusPanelWidget() {
  const store = useCompanyDashboardStore()
  const { widgetData } = storeToRefs(store)
  const { t } = useI18n()

  const getDisplay = (widget: IWidget): IStatusPanelDisplay => {
    const dataState = widgetData.value[widget.id]

    if (!dataState || dataState.loading) {
      return {
        state: 'loading',
        label: '...',
        accentColor: '#64748B',
        bgClass: 'bg-gray-400',
      }
    }

    if (dataState.error || dataState.value === null) {
      return {
        state: 'error',
        label: '--',
        accentColor: '#64748B',
        bgClass: 'bg-gray-400',
      }
    }

    const rounded = Math.round(dataState.value)

    if (rounded === 0) {
      return {
        state: 'open',
        label: t('custom_dashboards.view.status_panel.open'),
        accentColor: DOOR_OPEN_STATE_COLORS[DOOR_OPEN_STATE_OPEN],
        bgClass: 'bg-red',
      }
    }

    if (rounded === 1) {
      return {
        state: 'closed',
        label: t('custom_dashboards.view.status_panel.closed'),
        accentColor: DOOR_OPEN_STATE_COLORS[DOOR_OPEN_STATE_CLOSED],
        bgClass: 'bg-yellow-400',
      }
    }

    return {
      state: 'unknown',
      label: '--',
      accentColor: '#64748B',
      bgClass: 'bg-gray-400',
    }
  }

  const isLoading = (widget: IWidget): boolean => {
    return widgetData.value[widget.id]?.loading ?? false
  }

  const hasError = (widget: IWidget): boolean => {
    const state = widgetData.value[widget.id]
    if (!state || state.loading) return false
    return state.error || state.value === null
  }

  return {
    getDisplay,
    isLoading,
    hasError,
    ACCENT_COLOR: STATUS_PANEL_ACCENT_COLOR,
  }
}
